/* ============================================================================
 * ALTIORA · Institution portal (Phase B · Q3c model choice)
 * ----------------------------------------------------------------------------
 * 3 états :
 *  - not-onboarded → Onboarding modal with 3 model choices
 *  - named / hybrid → beneficiaries table + invite CTA + M7 cohort embed
 *  - aggregate     → M7 cohort with anonymized report only (no named users)
 * ==========================================================================*/
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  Building2, Users, Radar, Lock, ArrowRight, Loader2, Plus, X, Info,
  CheckCircle2, ShieldCheck, Mail,
} from "lucide-react";

const ORG_TYPES = [
  ["ministry", "Ministère"],
  ["university", "Université / École"],
  ["ciusss", "CIUSSS / Santé publique"],
  ["ngo", "Organisme communautaire"],
  ["corporate", "Entreprise"],
];

const MODELS = [
  {
    key: "named",
    title: "Bénéficiaires nommés (SaaS B2B)",
    desc: "Vous invitez chacun de vos bénéficiaires par email. Chacun reçoit son propre compte et son propre diagnostic. Vous voyez leur progression (avec consentement).",
    icon: Users,
    for_fr: "Recommandé pour : programmes de formation, mentorat structuré, accompagnement 1-à-1.",
  },
  {
    key: "aggregate",
    title: "Rapports agrégés anonymes",
    desc: "Aucun bénéficiaire nommé. Vous accédez uniquement à des cohortes anonymisées (k ≥ 20). Aucune donnée identifiable.",
    icon: Lock,
    for_fr: "Recommandé pour : recherche, statistique publique, transparence maximale.",
  },
  {
    key: "hybrid",
    title: "Modèle hybride",
    desc: "Combinaison des deux : invitations nommées + tableaux agrégés en parallèle. Pour institutions matures.",
    icon: Radar,
    for_fr: "Recommandé pour : ministères, grands centres de recherche, réseaux nationaux.",
  },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { delay: d, duration: 0.6 } },
});

export default function InstitutionPortal() {
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institution/me");
      setOrg(data);
      if (!data.onboarded) {
        setShowOnboard(true);
      } else {
        // Parallel fetch cohort + beneficiaries
        const [c, b] = await Promise.all([
          api.get("/diagnostic/cohort/territorial").catch(() => null),
          data.model !== "aggregate"
            ? api.get("/institution/beneficiaries").catch(() => null)
            : Promise.resolve(null),
        ]);
        setCohort(c?.data || null);
        setBeneficiaries(b?.data?.items || []);
      }
    } catch (err) {
      toast.error("Impossible de charger le portail.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F3]">
        <TopBar />
        <div className="max-w-6xl mx-auto px-6 py-14"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      </div>
    );
  }

  const onboarded = org?.onboarded;

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="institution-portal">
      <TopBar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Hero */}
        <motion.div {...fade(0)}>
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#7A5B14] mb-3 inline-flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> Portail institution
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-navy tracking-tight leading-[1.05]">
            {onboarded ? org.name : "Bienvenue"}
          </h1>
          {onboarded && (
            <p className="mt-3 text-base text-[#374151]">
              Modèle actif : <span className="font-medium text-navy">
                {MODELS.find((m) => m.key === org.model)?.title}
              </span>
            </p>
          )}
        </motion.div>

        {/* Onboarded content */}
        {onboarded && (
          <>
            {/* KPI cohorte M7 */}
            <motion.div {...fade(0.15)} className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="institution-cohort">
              {cohort?.answer?.cohort_available ? (
                <>
                  <KpiTile label="Cohorte anonymisée" value={cohort.answer.n} sub="individus (k ≥ 20)" />
                  <KpiTile label="IMES moyen" value={cohort.answer.avg_imes} sub="/100" highlight />
                  <KpiTile label="IMES médian" value={cohort.answer.median_imes} sub="/100" />
                </>
              ) : (
                <div className="md:col-span-3 border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg p-6 text-center">
                  <Lock className="h-6 w-6 text-amber-700 mx-auto mb-2" />
                  <p className="text-sm text-amber-900 font-medium">Cohorte insuffisante — anonymat préservé</p>
                  <p className="text-xs text-amber-800 mt-1">
                    {cohort?.answer?.reason_fr || "Vos bénéficiaires n'ont pas encore complété assez de diagnostics."}
                  </p>
                </div>
              )}
            </motion.div>

            {/* Bénéficiaires (si modèle named / hybrid) */}
            {org.model !== "aggregate" && (
              <motion.div {...fade(0.25)} className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-2xl text-navy">Bénéficiaires invités</h2>
                  <button
                    onClick={() => setShowInvite(true)}
                    data-testid="btn-invite-beneficiary"
                    className="inline-flex items-center gap-2 bg-navy hover:bg-[#123163] text-white text-sm font-medium px-4 py-2 rounded-md transition"
                  >
                    <Plus className="h-4 w-4" /> Inviter un bénéficiaire
                  </button>
                </div>
                {beneficiaries.length === 0 ? (
                  <div className="border-2 border-dashed border-[#E4E1D8] bg-white rounded-lg p-8 text-center">
                    <Mail className="h-8 w-8 text-[#8B8B8F] mx-auto mb-3" />
                    <p className="text-sm text-[#5B5B62]">Aucun bénéficiaire invité pour le moment.</p>
                    <p className="text-xs text-[#8B8B8F] mt-1">Les invitations créent un compte membre lié à votre institution.</p>
                  </div>
                ) : (
                  <div className="border border-[#E4E1D8] bg-white rounded-lg overflow-hidden" data-testid="beneficiaries-table">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F1EFE9] border-b border-[#E4E1D8]">
                        <tr>
                          <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Nom</th>
                          <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Email</th>
                          <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Statut</th>
                          <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">Ajouté</th>
                        </tr>
                      </thead>
                      <tbody>
                        {beneficiaries.map((b) => (
                          <tr key={b.invite_id} className="border-b border-[#F1EFE9]" data-testid={`beneficiary-${b.invite_id}`}>
                            <td className="px-4 py-3 text-navy">{[b.first_name, b.last_name].filter(Boolean).join(" ") || "—"}</td>
                            <td className="px-4 py-3 text-[#374151]">{b.email}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded ${
                                b.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-[#F1EFE9] text-[#7A5B14]"
                              }`}>{b.status}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-[#8B8B8F] font-mono">{new Date(b.created_at).toLocaleDateString("fr")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}

            {/* Trusted Intelligence footer */}
            <motion.div {...fade(0.35)} className="mt-10 border border-[#E4E1D8] bg-white rounded-lg p-5 flex items-start gap-3">
              <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#374151] leading-relaxed">
                Toutes les données individuelles restent chiffrées. Les cohortes agrégées respectent une k-anonymité ≥ 20. Un audit d'implémentation peut être fourni sur demande — voir <a href="/securite" className="underline">page sécurité</a>.
              </p>
            </motion.div>
          </>
        )}
      </div>

      {/* Onboarding modal */}
      <AnimatePresence>
        {showOnboard && (
          <OnboardModal
            onDone={() => { setShowOnboard(false); load(); }}
          />
        )}
        {showInvite && (
          <InviteModal onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function KpiTile({ label, value, sub, highlight = false }) {
  return (
    <div className={`border-2 rounded-lg p-4 ${highlight ? "border-navy bg-[#F5F4F0]" : "border-[#E4E1D8] bg-white"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">{label}</div>
      <div className="mt-2 font-display text-3xl md:text-4xl text-navy tabular-nums">
        {value}<span className="text-lg text-[#8B8B8F] ml-1">{sub}</span>
      </div>
    </div>
  );
}

function OnboardModal({ onDone }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    org_name: "", org_type: "ministry", model: "named",
    expected_beneficiaries: 0, contact_email: "", description_fr: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.org_name || !form.contact_email) { toast.error("Nom et email requis"); return; }
    setBusy(true);
    try {
      await api.post("/institution/onboard", form);
      toast.success("Institution configurée.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'onboarding");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="onboard-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          <div className="text-xs font-mono uppercase tracking-widest text-[#7A5B14] mb-2">
            Onboarding · Étape {step}/2
          </div>
          <h2 className="font-display text-3xl text-navy leading-tight mb-6">
            {step === 1 ? "Choisissez votre modèle" : "Votre institution"}
          </h2>

          {step === 1 && (
            <div className="space-y-3" data-testid="model-choices">
              {MODELS.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setForm((f) => ({ ...f, model: m.key }))}
                  data-testid={`model-choice-${m.key}`}
                  className={`w-full text-left p-5 rounded-lg border-2 transition ${
                    form.model === m.key
                      ? "border-navy bg-[#F5F4F0]"
                      : "border-[#E4E1D8] bg-white hover:border-[#B8B5AC]"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <m.icon className="h-5 w-5 text-navy flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="font-display text-lg text-navy mb-1">{m.title}</div>
                      <p className="text-sm text-[#374151] leading-relaxed mb-2">{m.desc}</p>
                      <p className="text-xs text-[#7A5B14] font-medium">{m.for_fr}</p>
                    </div>
                    {form.model === m.key && <CheckCircle2 className="h-5 w-5 text-navy" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="org-info-form">
              <Field label="Nom de l'institution">
                <input value={form.org_name} onChange={(e) => setForm((f) => ({ ...f, org_name: e.target.value }))}
                  data-testid="input-org-name" className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" />
              </Field>
              <Field label="Type">
                <select value={form.org_type} onChange={(e) => setForm((f) => ({ ...f, org_type: e.target.value }))}
                  data-testid="input-org-type" className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md bg-white">
                  {ORG_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Email de contact">
                <input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                  data-testid="input-contact-email" className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" />
              </Field>
              <Field label="Nombre de bénéficiaires prévus (estimation)">
                <input type="number" min="0" value={form.expected_beneficiaries}
                  onChange={(e) => setForm((f) => ({ ...f, expected_beneficiaries: Number(e.target.value) }))}
                  data-testid="input-expected" className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" />
              </Field>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            {step === 2 ? (
              <button onClick={() => setStep(1)} className="text-sm text-[#5B5B62] hover:text-navy">← Retour</button>
            ) : <div />}
            <button
              onClick={() => step === 1 ? setStep(2) : submit()}
              disabled={busy}
              data-testid="onboard-next-btn"
              className="inline-flex items-center gap-2 bg-navy hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md transition disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {step === 1 ? "Continuer" : "Créer mon institution"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function InviteModal({ onClose, onDone }) {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", tag_fr: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email) { toast.error("Email requis"); return; }
    setBusy(true);
    try {
      await api.post("/institution/beneficiary", form);
      toast.success("Bénéficiaire invité.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'invitation");
    } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="invite-modal">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl text-navy">Inviter un bénéficiaire</h3>
            <button onClick={onClose} className="text-[#8B8B8F] hover:text-navy"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-3">
            <Field label="Email"><input type="email" data-testid="invite-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" /></Field>
            <Field label="Prénom"><input data-testid="invite-first-name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" /></Field>
            <Field label="Nom"><input data-testid="invite-last-name" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" /></Field>
            <Field label="Étiquette (cohorte, programme…)"><input data-testid="invite-tag" value={form.tag_fr} onChange={(e) => setForm((f) => ({ ...f, tag_fr: e.target.value }))} className="w-full px-3 py-2 border border-[#E4E1D8] rounded-md" /></Field>
          </div>
          <button onClick={submit} disabled={busy} data-testid="invite-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md transition disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer l'invitation
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest text-[#5B5B62] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
